import React from 'react';
import type { ButtonProps, SxProps, Theme } from '@mui/material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import { X as CloseIcon } from '@phosphor-icons/react/dist/ssr';

interface ProfessionalDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  /** Rendered below the title row, still inside the non-scrolling header */
  headerExtra?: React.ReactNode;
  /** Ref forwarded to the scrollable DialogContent element */
  contentRef?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
  actions?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  variant?: 'default' | 'elevated' | 'outlined' | 'modern' | 'professional';
  sx?: SxProps<Theme>;
  headerSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  showDivider?: boolean;
  autoHeight?: boolean;
  animation?: 'fade' | 'slide' | 'zoom';
  disableBackdropClick?: boolean;
  showCloseButton?: boolean;
}

export default function ProfessionalDialog({
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerExtra,
  contentRef,
  children,
  actions,
  maxWidth = 'md',
  fullWidth = true,
  sx,
  headerSx,
  contentSx,
  actionsSx,
  showDivider = true,
  autoHeight = false,
  disableBackdropClick = false,
  showCloseButton = true,
}: ProfessionalDialogProps) {
  const theme = useTheme();

  return (
    <Dialog
      BackdropProps={{
        sx: {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.4)',
        },
      }}
      PaperProps={{
        sx: {
          maxHeight: autoHeight ? '95vh' : '90vh',
          height: 'auto',
          minHeight: autoHeight ? 'auto' : '40vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          backgroundColor: theme.palette.background.paper,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          boxShadow: theme.shadows[8],
          ...sx,
        },
      }}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      onClose={disableBackdropClick ? undefined : onClose}
      open={open}
    >
      {/* Header — non-scrolling; matches the page header strips */}
      <Box
        sx={{
          flexShrink: 0,
          borderBottom: headerExtra ? 'none' : '1px solid',
          borderColor: 'divider',
          ...headerSx,
        }}
      >
        {/* Title row */}
        <Box
          sx={{
            px: { xs: 2.5, sm: 3 },
            py: 2.5,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            borderBottom: headerExtra ? '1px solid' : 'none',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="h2"
              sx={{ fontWeight: 700, lineHeight: 1.25, mb: subtitle ? 0.5 : 0 }}
              variant="h5"
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>

          {showCloseButton && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: 'text.secondary',
                mt: -0.5,
                mr: -0.5,
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
              }}
            >
              <CloseIcon size={18} />
            </IconButton>
          )}
        </Box>

        {/* Optional extra header content (e.g. a stepper) */}
        {headerExtra && (
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
            {headerExtra}
          </Box>
        )}
      </Box>

      {/* Content */}
      <DialogContent
        ref={contentRef}
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 3,
          backgroundColor: 'transparent',
          flex: autoHeight ? 'none' : 1,
          overflow: autoHeight ? 'visible' : 'auto',
          minHeight: 0,
          m: 0,
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {/* Actions */}
      {actions && (
        <>
          {showDivider && <Divider />}
          <DialogActions
            sx={{
              justifyContent: 'flex-end',
              px: { xs: 2.5, sm: 3 },
              py: 2,
              m: 0,
              gap: 1.5,
              ...actionsSx,
            }}
          >
            {actions}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

interface DialogActionButtonsProps {
  onCancel: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  disabled?: boolean;
  confirmButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  variant?: 'default' | 'destructive' | 'success';
}

export function DialogActionButtons({
  onCancel,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loading = false,
  disabled = false,
  confirmButtonProps,
  cancelButtonProps,
  variant = 'default',
}: DialogActionButtonsProps) {
  const getConfirmButtonColor = () => {
    switch (variant) {
      case 'destructive':
        return 'error';
      case 'success':
        return 'success';
      default:
        return 'primary';
    }
  };

  return (
    <>
      <Button
        disabled={loading || disabled}
        onClick={onCancel}
        variant="outlined"
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
        {...cancelButtonProps}
      >
        {cancelText}
      </Button>
      <Button
        color={getConfirmButtonColor() as any}
        disabled={loading || disabled}
        onClick={onConfirm}
        variant="contained"
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' }}
        {...confirmButtonProps}
      >
        {confirmText}
      </Button>
    </>
  );
}
